import { Injectable } from '@angular/core';
import { StorageService } from '@service/storage.service';
import { AuthResponse, TokenInfoResponse, TokenResponse } from '@model/auth';
import { TokenClient } from '@helper/token-client';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable, Subscription, interval } from 'rxjs';
import { AuthService } from './auth.service';
import { ApiClient } from '@helper/api-client';

const key: string = 'token';

interface RefreshableToken {
  token: string;
  expire: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class TokenStorageService {
  private refreshToken: RefreshableToken | null = null;
  private accessToken: RefreshableToken | null = null;

  private refreshTokenSubscription: Subscription | null = null;
  private refreshTokenInfoSubscription: Subscription | null = null;
  private accessTokenSubscription: Subscription | null = null;

  constructor(
    private storageService: StorageService,
    private authService: AuthService,
    private httpClient: HttpClient,
    private apiClient: ApiClient,
  ) {
    this.setRefreshToken(storageService.getValue(key));
  }

  getAuth(state: string, sessionState: string, code: string): Observable<AuthResponse> {
    return this.apiClient.post(environment.api + "/auth", {
      state: state,
      sessionState: sessionState,
      code: code,
    });
  }

  setRefreshToken(token: string | null, expire: number | null = null) {
    if (token === null) {
      this.storageService.removeKey(key);
      this.refreshToken = null;
      this.accessToken = null;

      this.authService.clear();
      this.apiClient.setToken(null);
    } else {
      this.storageService.setValue(key, token);
      this.refreshToken = {
        token: token,
        expire: expire ? expire * 1000 : null,
      }

      if (!expire) {
        this.getRefreshTokenInfo();
      } else {
        this.startRefreshingRefreshToken();
      }

      if (this.accessToken === null) {
        this.refreshAccessToken();
      }
    }
  }

  private getRefreshTokenInfo() {
    if (!this.refreshToken || (this.refreshToken.expire && Date.now() > this.refreshToken.expire)) {
      return;
    }

    var tokenClient = new TokenClient(this.httpClient);

    tokenClient.setToken(this.refreshToken.token);

    var subscription = tokenClient.get<TokenInfoResponse>(environment.api + '/auth/token/info').subscribe({
      next: (rsp: TokenInfoResponse) => {
        subscription.unsubscribe();

        if (this.refreshToken) {
          this.refreshToken.expire = rsp.expire * 1000;
        }

        this.startRefreshingRefreshToken();
      },
      error: (err: HttpErrorResponse) => {
        subscription.unsubscribe();

        if (err.status !== 403 && err.status !== 401) {
          if (this.refreshTokenSubscription) {
            this.refreshTokenSubscription.unsubscribe();
          }
          this.refreshTokenInfoSubscription = interval(10000).subscribe(() => this.refreshTokenInfoEvent());
        } else {
          this.setRefreshToken(null);
        }
      },
    });
  }

  private refreshTokenInfoEvent() {
    if (this.refreshTokenInfoSubscription) {
      this.refreshTokenInfoSubscription.unsubscribe();
      this.refreshTokenInfoSubscription = null;
    }

    this.getRefreshTokenInfo();
  }

  private startRefreshingRefreshToken() {
    if (!this.refreshToken || !this.refreshToken.expire) {
      return;
    }

    const diff: number = this.refreshToken.expire - Date.now() - 10 * 60 * 1000;
    if (diff > 0) {
      if (this.refreshTokenSubscription) {
        this.refreshTokenSubscription.unsubscribe();
      }
      this.refreshTokenSubscription = interval(diff).subscribe(() => this.refreshRefreshToken());
    } else {
      this.refreshRefreshToken();
    }
  }

  private refreshRefreshToken() {
    if (this.refreshTokenSubscription) {
      this.refreshTokenSubscription.unsubscribe();
      this.refreshTokenSubscription = null;
    }

    if (!this.refreshToken) {
      return;
    }

    var tokenClient = new TokenClient(this.httpClient);

    tokenClient.setToken(this.refreshToken.token);

    var subscription = tokenClient.get<TokenResponse>(environment.api + '/auth/token/refresh').subscribe({
      next: (rsp: TokenResponse) => {
        subscription.unsubscribe();
        this.setRefreshToken(rsp.token, rsp.expire);
      },
      error: () => {
        subscription.unsubscribe();

        if (!this.refreshToken || !this.refreshToken.expire || this.refreshToken.expire < Date.now()) {
          this.setRefreshToken(null);
        } else {
          if (this.refreshTokenSubscription) {
            this.refreshTokenSubscription.unsubscribe();
          }
          this.refreshTokenSubscription = interval(10000).subscribe(() => this.refreshRefreshToken());
        }
      },
    });
  }

  private refreshAccessToken() {
    if (this.accessTokenSubscription) {
      this.accessTokenSubscription.unsubscribe();
      this.accessTokenSubscription = null;
    }

    if (!this.refreshToken) {
      return;
    }

    var tokenClient = new TokenClient(this.httpClient);

    tokenClient.setToken(this.refreshToken.token);

    var subscription = tokenClient.get<TokenResponse>(environment.api + '/auth/token/access').subscribe({
      next: (rsp: TokenResponse) => {
        subscription.unsubscribe();

        this.accessToken = {
          token: rsp.token,
          expire: rsp.expire * 1000,
        }

        this.startRefreshingAccessToken();

        this.authService.setTokenResponse(rsp);
        this.apiClient.setToken(rsp.token);
      },
      error: (err: HttpErrorResponse) => {
        subscription.unsubscribe();

        if (err.status != 400 && err.status !== 401 && err.status !== 403) {
          if (this.accessTokenSubscription) {
            this.accessTokenSubscription.unsubscribe();
          }
          this.accessTokenSubscription = interval(10000).subscribe(() => this.refreshAccessToken());
        } else {
          this.setRefreshToken(null);
        }
      },
    });
  }

  startRefreshingAccessToken() {
    if (!this.accessToken || !this.accessToken.expire) {
      return;
    }

    const diff: number = this.accessToken.expire - Date.now() - 2 * 60 * 1000;
    if (diff > 0) {
      if (this.accessTokenSubscription) {
        this.accessTokenSubscription.unsubscribe();
      }
      this.accessTokenSubscription = interval(diff).subscribe(() => this.refreshAccessToken());
    } else {
      this.refreshAccessToken();
    }
  }

  isLogged(): boolean | null {
    if (this.refreshToken === null) {
      return false;
    }

    if (this.accessToken === null) {
      return null;
    }

    return true;
  }
}
